import React, { useEffect, useState, useMemo, useRef } from "react"
import { FiEdit, FiPlus, FiTrash2 } from "react-icons/fi"
import Pagination from "../../../../Components/Pagination"
import { Link, useNavigate } from "react-router-dom"
import createAxiosInstance from "../../../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../../../utils/api";
import useAgencies from "../../../../hooks/useAgencies"
import useMyAgencyData from "../../../../hooks/useMyAgencyData"
import { AgencyResponses } from "../../../../type/Agency";
import usePageFilter from "../../../../hooks/usePageFilter"
import { useFeature } from "../../../../hooks/useFeature"
import { showToast } from "../../../../Components/ToastNotification"
import Loader from "../../../../Components/Loader"
import useSocket from "../../../../services/socketService"
import { formatDateTime } from "../../../../utils/formatters";

interface Response {
  responseId: string;
  response: string;
}

interface Request {
  requestId: string;
  responses: Response[];
  status: string;
  createdAt: string;
}

const RequestSent = () => {
  const isSender = useFeature("bedfinder:canSendRequest");
  const navigate = useNavigate();
  const myShelter = useMyAgencyData();
  const { agenciesById: agencies } = useAgencies();
  const axiosInstance = createAxiosInstance();

  const [request, setRequest] = useState([]);
  const requestRef = useRef<Request[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowPerPage, setRowPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoding, setIsLoding] = useState(true);

  const { on } = useSocket("requests");

  useEffect(() => {
    requestRef.current = request;
  }, [request]);

  useEffect(() => {
    const unsubscribe = on("message", (data) => {
      if (data.eventType === "success" || data.eventType === "reject") {
        const isFirstResponse = requestRef.current?.some((req) =>
          req.requestId === data.data.requestId &&
          req.responses.some(
            (response: Response) =>
              response.responseId === data.data.responseId &&
              response.response === "null"
          )
        );
  
        if (isFirstResponse) {
          showToast("An agency responded to your request.", "info");
        } else {
          showToast("An agency changed a response.", "info");
        }
        getMyRequests();
      }
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (myShelter) {
      getMyRequests();
    }
  }, [myShelter]);

  const totalRequests = useMemo(
    () =>
      request.map((req: any) => ({
        ...req,
        referenceUrl: `/bedfinder/my-requests/review/${req.requestId}`,
        agencyResponses: req.responses
          ?.filter((response: any) => {
            const agency = agencies[response.agencyId];
            return agency?.groupInfo?.some(
              (currentInfo: any) =>
                req?.groupId?.includes(currentInfo.groupId) &&
                currentInfo.canAnswerRequest &&
                currentInfo.groupId != myShelter?.agencyId,
            );
          })
          .filter((response: any) => response.agencyId !== myShelter?.agencyId)
          .reduce(
            (agencyResponses: any, response: any) => {
              if (response.response === "null") agencyResponses.null++;
              else if (response.response === "no") agencyResponses.no++;
              else agencyResponses.possible++;
              return agencyResponses;
            },
            {
              possible: 0,
              no: 0,
              null: 0,
            } as { [type in keyof AgencyResponses]: number },
          ),
      })).sort((a: any, b: any) => b.nr - a.nr),
    [request, agencies],
  );

  const filteredRequests = usePageFilter(
    totalRequests,
    (currentPage - 1) * rowPerPage,
    rowPerPage
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const getMyRequests = async () => {
    try {
      const response = await axiosInstance.get(`${API_ENDPOINTS.BEDFINDER.GETMYREQUEST}?status=*&expand=need,responses`);

      const data = response.data; // Extract the actual data

      setRequest(data);
      setTotalPages(Math.ceil(data.length / rowPerPage));
      setIsLoding(false);
    } catch (error) {
      setIsLoding(false);
      console.log(error);
    }
  };

  const handleOnClick = () => {
    isSender
      ? navigate("/bedfinder/new-request")
      : showToast(
        "You don't have permission to create a new request. Please contact your administrator.", "error"
      );
  }

  return (
    <>
      {isLoding && <Loader />}
      <div className=" bg-white">
        <div className="container py-4 md:py-10 2xl:py-[70px]">
          <div className="mb-5 2xl:mb-10 flex justify-center">
            <button onClick={handleOnClick} className="btn btn-primary bg-[#DD5226] hover:bg-orange-400 rounded-full">
              <FiPlus size={20} /> Find a Bed
            </button>
          </div>
          <div className="mb-4">
            <h3 className="text-gray-900 font-semibold text-base lg:text-lg 2xl:text-xl mb-2">All Bed Requests Sent</h3>
          </div>
          <div className="relative md:overflow-hidden md:border md:border-borderGray rounded-xl">
            <table className="table_responsive_block w-full text-xs 2xl:text-sm text-left rtl:text-right">
              <thead className="text-xs 2xl:text-sm text-gray-600 bg-gray-100 border-b">
                <tr>
                  <th scope="col" className="px-4 2xl:px-6 py-3 font-medium">Request #</th>
                  <th scope="col" className="px-4 2xl:px-6 py-3 font-medium">Date/Time</th>
                  <th scope="col" className="px-4 2xl:px-6 py-3 font-medium">Reference ID</th>
                  <th scope="col" className="px-4 2xl:px-6 py-3 font-medium"># Possible</th>
                  <th scope="col" className="px-4 2xl:px-6 py-3 font-medium"># No</th>
                  <th scope="col" className="px-4 2xl:px-6 py-3 font-medium"># No Reply</th>
                  <th scope="col" className="px-4 2xl:px-6 py-3 font-medium">Status</th>
                  <th scope="col" className="px-4 2xl:px-6 py-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {filteredRequests.map((request: any, index: number) => (
                  <tr className={` border-b ${index % 2 == 0 ? 'bg-white' : 'bg-gray-50'}`} key={index}>
                    <td data-title='Request #' scope="row" className="px-4 2xl:px-6 py-3 font-medium text-primary cursor-pointer whitespace-nowrap">
                      <Link to={request.referenceUrl}> {request.nr} </Link>
                    </td>
                    <td data-title='Date/Time' className="px-4 2xl:px-6 py-3">{formatDateTime(request.createdAt)}</td>
                    <td data-title='Reference ID' className="px-4 2xl:px-6 py-3">{request.need.caseReferenceId}</td>
                    <td data-title='# Possible' className="px-4 2xl:px-6 py-3">{request.agencyResponses.possible}</td>
                    <td data-title='# No' className="px-4 2xl:px-6 py-3">{request.agencyResponses.no}</td>
                    <td data-title='# No Reply' className="px-4 2xl:px-6 py-3">{request.agencyResponses.null}</td>
                    <td data-title='Status' className="px-4 2xl:px-6 py-3">
                      {request.status === "open" && (
                        <span className="bg-success50 text-success text-xs 2xl:text-sm p-1 px-2 rounded-full flex items-center gap-1 w-fit font-medium">
                          Open
                        </span>
                      )}
                      {request.status === "closed" && (
                        <span className="bg-orange-50 text-error text-xs 2xl:text-sm p-1 px-2 rounded-full flex items-center gap-1 w-fit font-medium">
                          Closed
                        </span>
                      )}
                    </td>
                    <td data-title='Action' className="px-4 2xl:px-6 py-3 flex space-x-1">
                      <Link to={request.referenceUrl}><button className="text-gray-700 text-xl size-10 flex items-center justify-center rounded-lg hover:bg-gray-100"><FiEdit /></button></Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-5 flex justify-between items-center">
            <div></div>
            <Pagination totalPages={totalPages} onPageChange={handlePageChange} />
          </div>
        </div>
      </div>
    </>
  )
}

export default RequestSent